// Tradução de uma linha do Log de Auditoria para português (issue #42).
//
// Puro e sem DOM: recebe a linha e o Snapshot, devolve texto. É onde mora a
// decisão do ADR-0012 de resolver os ids **na leitura** — a linha antiga passa
// a mostrar o nome ATUAL da entidade, e a apagada aparece com marcador. A
// alternativa (gravar os nomes junto no `detalhe`) seria historicamente mais
// fiel, mas exigiria lógica por agregado no caminho de escrita.

import { formatReais } from "../../domain/money.ts";
import type { Snapshot } from "../../domain/snapshot.ts";

/** Verbo de cada `agregado · tipo`. O que não estiver aqui cai no cru. */
const VERBOS: Record<string, string> = {
  "lote · lancar": "Lançou a OS",
  "lote · editar": "Editou a OS",
  "lote · gravarFicha": "Gravou a Ficha da OS",
  "lote · excluir": "Excluiu a OS",
  "lote · cancelar": "Cancelou a OS",
  "lote · lancarEmMassa": "Importou OS em massa",
  "devolucao · registrar": "Fechou a OS",
  "perda · registrar": "Registrou uma perda",
  "perda · excluir": "Excluiu uma perda",
  "fatura · criar": "Abriu um rascunho de fatura",
  "fatura · criarEmMassa": "Abriu faturas em massa",
  "fatura · editarPeriodo": "Mudou o período da fatura",
  "fatura · adicionarAjuste": "Ajustou a fatura",
  "fatura · removerAjuste": "Removeu um ajuste da fatura",
  "fatura · adicionarCompensacao": "Compensou peças na fatura",
  "fatura · removerCompensacao": "Removeu uma compensação de peças da fatura",
  "fatura · emitir": "Emitiu a fatura",
  "fatura · reabrir": "Reabriu a fatura",
  "fatura · excluir": "Excluiu o rascunho de fatura",
  "cliente · criar": "Cadastrou o cliente",
  "cliente · editar": "Editou o cliente",
  "cliente · definirAtivo": "Ativou/inativou o cliente",
  "categoria · criar": "Criou a categoria",
  "categoria · editar": "Renomeou a categoria",
  "config · dataFaturamento": "Mudou a data de referência do faturamento",
  "config · rodapeExport": "Editou o rodapé das exportações",
  "config · temposReferencia": "Editou os tempos de referência",
  "lista · definir": "Editou um vocabulário (motivos, funções…)",
  "funcionario · criar": "Cadastrou um funcionário",
  "insumo · criar": "Cadastrou um produto/insumo",
  "tipoPeca · criar": "Cadastrou o tipo de peça",
  "tipoPeca · editar": "Editou o tipo de peça",
  "preco · aplicarTabela": "Aplicou preços",
  "preco · corrigirVigenciaBase": "Corrigiu uma vigência de preço",
  "preco · removerVigenciaBase": "Removeu uma vigência de preço",
  "preco · corrigirVigenciaCliente": "Corrigiu uma vigência de preço do cliente",
  "preco · removerVigenciaCliente": "Removeu uma vigência de preço do cliente",
  "preco · limparTudo": "APAGOU todo o histórico de preços",
  "conta · criar": "Criou a conta",
  "conta · editar": "Editou a conta",
  "conta · excluir": "Excluiu a conta",
  "conta · registrarPagamento": "Registrou um pagamento",
  "conta · removerPagamento": "Removeu um pagamento",
  "categoriaFinanceira · criar": "Criou a categoria financeira",
  "retirada · registrar": "Registrou uma retirada pessoal",
  "retirada · editar": "Editou uma retirada pessoal",
  "retirada · excluir": "Excluiu uma retirada pessoal",
  "responsavel · criar": "Cadastrou um responsável",
  "usuario · criar": "Criou o usuário",
  "usuario · redefinir-senha": "Redefiniu uma senha",
  "usuario · definirAtivo": "Ativou/inativou um usuário",
  "usuario · excluir": "Excluiu um usuário",
  "sessao · login": "Entrou no sistema",
  "sessao · login-falho": "Tentativa de login falhou",
  "sessao · logout": "Saiu do sistema",
  "banco · download": "Baixou o banco de dados",
  "comando · rejeitado": "Comando recusado",
};

/**
 * Módulo de cada agregado — a coluna "Ação" da tela. O agregado é o nome
 * interno (`tipoPeca`); aqui ele vira o nome do módulo como a pessoa o conhece
 * no menu. O que não estiver aqui aparece cru, igual aos verbos.
 */
const MODULOS: Record<string, string> = {
  lote: "OS",
  devolucao: "Fechamento de OS",
  perda: "Backlog",
  fatura: "Faturamento",
  cliente: "Clientes",
  categoria: "Tipos de Peça",
  tipoPeca: "Tipos de Peça",
  preco: "Tabela de Preços",
  funcionario: "Funcionários",
  insumo: "Produtos/Insumos",
  conta: "Contas a Pagar/Receber",
  categoriaFinanceira: "Categorias Financeiras",
  retirada: "Retiradas Pessoais",
  responsavel: "Retiradas Pessoais",
  usuario: "Usuários",
  sessao: "Acesso",
  config: "Parâmetros do Sistema",
  lista: "Parâmetros do Sistema",
  banco: "Backup",
  comando: "Sistema",
};

/** O agregado do `resumo` ("lote · lancar" → "lote"). */
export function agregadoDe(resumo: string): string {
  return resumo.split("·")[0]!.trim();
}

/** Nome do módulo de um agregado, para a coluna "Ação" e para o filtro. */
export function moduloLegivel(agregado: string): string {
  return MODULOS[agregado] ?? agregado;
}

/** Nome de uma entidade pelo id, com marcador quando não existe mais. */
function nomeDe(snap: Snapshot, id: unknown): string | null {
  if (typeof id !== "string" || id === "") return null;
  const cli = snap.clientes.find((c) => c.id === id);
  if (cli) return cli.nome;
  const tp = snap.tiposPeca.find((t) => t.id === id);
  if (tp) return tp.nome;
  const u = snap.usuarios.find((x) => x.id === id);
  if (u) return u.nome;
  const l = snap.lotes.find((x) => x.id === id);
  if (l) return `OS ${l.os}`;
  const f = snap.faturas.find((x) => x.id === id);
  if (f) return `Fatura ${f.numero ?? "rascunho"}`;
  const r = snap.responsaveis.find((x) => x.id === id);
  if (r) return r.nome;
  const ct = snap.contas.find((x) => x.id === id);
  if (ct) return ct.descricao;
  // parece um id, mas nada casa: foi apagado desde então
  return id.length >= 8 ? `${id.slice(0, 8)}… (removido)` : null;
}

function ehChaveDeDinheiro(chave: string): boolean {
  return /centavos$/i.test(chave);
}

/**
 * Os detalhes que valem mostrar na linha: nomes resolvidos e dinheiro
 * formatado. Percorre o `detalhe` raso — o JSON inteiro fica no "ver JSON".
 */
export function detalhesLegiveis(snap: Snapshot, detalhe: unknown): string[] {
  const out: string[] = [];
  const visitar = (valor: unknown, profundidade: number): void => {
    if (profundidade > 2 || valor == null || typeof valor !== "object") return;
    for (const [chave, v] of Object.entries(valor as Record<string, unknown>)) {
      if (chave === "cmd" || chave === "dados" || chave === "comando") {
        visitar(v, profundidade + 1);
        continue;
      }
      if (ehChaveDeDinheiro(chave) && typeof v === "number") {
        out.push(formatReais(v));
        continue;
      }
      if (chave === "erro" && typeof v === "string") {
        out.push(v);
        continue;
      }
      if (chave === "login" && typeof v === "string") {
        out.push(`login "${v}"`);
        continue;
      }
      if (/id$/i.test(chave)) {
        const nome = nomeDe(snap, v);
        if (nome) out.push(nome);
        continue;
      }
      if (chave === "os" && typeof v === "number") out.push(`OS ${v}`);
      if (chave === "motivo" && typeof v === "string" && v) out.push(v);
    }
  };
  visitar(detalhe, 0);
  return [...new Set(out)].slice(0, 4);
}

export interface LinhaLegivel {
  /** Módulo onde a ação aconteceu (coluna "Ação"). */
  modulo: string;
  verbo: string;
  detalhes: string;
  /** O comando falhou? Pinta a linha de outro jeito. */
  erro: boolean;
}

export function linhaLegivel(
  snap: Snapshot,
  resumo: string,
  detalheJson: string,
): LinhaLegivel {
  let detalhe: unknown = null;
  try {
    detalhe = JSON.parse(detalheJson);
  } catch {
    /* detalhe ilegível: a linha ainda vale pelo resumo */
  }
  const erro =
    detalhe != null &&
    typeof detalhe === "object" &&
    (detalhe as { resultado?: unknown }).resultado === "erro";

  return {
    modulo: moduloLegivel(agregadoDe(resumo)),
    verbo: VERBOS[resumo] ?? resumo,
    detalhes: detalhesLegiveis(snap, detalhe).join(" · "),
    erro,
  };
}
